import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isConnectionOwned } from '@/components/OwnedChip'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getJSON } from '@/lib/api'
import type { CourseResponse } from '@/pages/CourseDetailPage'

export function EnrolmentCoursePicker({
  value,
  onChange,
  error,
}: {
  value: number | null
  onChange: (id: number | null) => void
  error: string
}) {
  const [search, setSearch] = useState('')
  const [title, setTitle] = useState('')
  const query = useQuery({
    queryKey: ['courses', 'enrolment-picker', search],
    queryFn: () =>
      getJSON<{ courses: CourseResponse['course'][] }>(
        `/api/v1/courses?status=active&owner=bright_huddle&search=${encodeURIComponent(search)}`,
      ),
  })
  const courses =
    query.data?.courses.filter(
      (course) => course.status === 'active' && !isConnectionOwned(course.owner),
    ) ?? []
  return (
    <FormField label="Course" htmlFor="enrolment-course" error={error}>
      <Input
        id="enrolment-course"
        placeholder="Search courses…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value)
          onChange(null)
          setTitle('')
        }}
      />
      {value && <p className="text-body-sm">{title}</p>}
      {!value && (
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {query.isPending && (
            <p className="text-body-sm text-muted-foreground">Loading courses…</p>
          )}
          {query.isError && <Alert>{query.error.message}</Alert>}
          {query.isSuccess && courses.length === 0 && (
            <p className="text-body-sm text-muted-foreground">No matching courses</p>
          )}
          {courses.map((course) => (
            <Button
              key={course.id}
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                setTitle(course.title)
                onChange(course.id)
              }}
            >
              {course.title}
            </Button>
          ))}
        </div>
      )}
    </FormField>
  )
}
